<script lang="ts" setup>
const props = defineProps({
  columnNames: {
    required: true,
    type: Array as PropType<string[]>,
  },
  columnPreview: {
    required: true,
    type: Array as PropType<string[]>,
  },
  headers: {
    required: true,
    type: Array as PropType<{ label: string, value: string }[]>,
  },
  schemaColumns: {
    required: true,
    type: Array as PropType<USelectMenuOption[]>,
  },
})

const fileColumnModel = defineModel<string>('fileColumn')
const schemaColumnModel = defineModel<string>('schemaColumn')

const previewCurrent = ref(0)

function onNavigatePreview(position: 'next' | 'previous') {
  if (position === 'next') {
    if (previewCurrent.value + 1 >= props.columnPreview.length) {
      previewCurrent.value = 0
    }
    else {
      previewCurrent.value += 1
    }
  }
  else if (position === 'previous') {
    if (previewCurrent.value - 1 <= 0) {
      previewCurrent.value = props.columnPreview.length - 1
    }
    else {
      previewCurrent.value -= 1
    }
  }
}

function onSchemaColumnChange(value: string) {
  schemaColumnModel.value = value
}

function onFileColumnChange(value: string) {
  fileColumnModel.value = value
}
</script>

<template>
  <div class="flex flex-col items-center text-center md:flex-row">
    <div
      v-for="(header, headerIndex) of headers"
      :key="header.value"
      class="relative flex w-full items-center p-4 text-sm md:h-16"
      :class="{
        'md:min-w-52 md:flex-1': header.value === 'preview',
        'md:w-52 md:min-w-52 md:flex-initial md:text-left': header.value !== 'preview' && header.value !== 'actions',
        'md:w-16 md:min-w-16 md:flex-initial': header.value === 'actions',
        'whitespace-nowrap': header.value !== 'match',
        'border-r border-gray-200 dark:border-gray-800': headerIndex !== headers.length - 1,
        'border-0': headerIndex === headers.length - 1,
      }"
    >
      <div
        v-if="header.value === 'name'"
        class="flex w-full flex-col"
      >
        <span class="flex size-full items-center justify-center">
          <USelectMenu
            class="w-full"
            :model-value="fileColumnModel"
            :options="columnNames.map(columnName => ({ label: columnName, value: columnName }))"
            :ui-menu="{ label: 'truncate' }"
            value-attribute="value"
            @change="onFileColumnChange"
          />
        </span>

        <span class="ml-1 flex items-start text-sm text-gray-500 md:hidden dark:text-gray-500">
          {{ header.label }}
        </span>
      </div>

      <div
        v-else-if="header.value === 'preview'"
        class="flex w-full flex-col"
      >
        <div class="flex size-full select-none items-center">
          <div
            v-if="columnPreview.length > 1"
            class="h-full w-8 flex-initial cursor-pointer items-center justify-center opacity-75 hover:text-white hover:opacity-100"
            @click="onNavigatePreview('previous')"
          >
            <UIcon
              class="size-4 flex-initial"
              :name="getIcon('chevronLeft')"
            />
          </div>

          <div class="flex-1 overflow-x-auto truncate text-center">
            {{ columnPreview[previewCurrent] }}
          </div>

          <div
            v-if="columnPreview.length > 1"
            class="h-full w-8 flex-initial cursor-pointer items-center justify-center opacity-75 hover:text-white hover:opacity-100"
            @click="onNavigatePreview('next')"
          >
            <UIcon
              class="size-4 flex-initial"
              :name="getIcon('chevronRight')"
            />
          </div>
        </div>

        <span class="ml-1 flex items-start text-sm text-gray-500 md:hidden dark:text-gray-500">
          {{ header.label }}
        </span>
      </div>

      <div
        v-else-if="header.value === 'match'"
        class="flex w-full flex-col"
      >
        <div class="flex size-full items-center justify-center">
          <USelectMenu
            class="w-full"
            :model-value="schemaColumnModel"
            :options="schemaColumns"
            :ui-menu="{ label: schemaColumnModel === 'choose' ? 'text-gray-500 dark:text-gray-500 truncate' : 'truncate' }"
            value-attribute="value"
            @change="onSchemaColumnChange"
          />
        </div>

        <span class="ml-1 flex items-start text-sm text-gray-500 md:hidden dark:text-gray-500">
          {{ header.label }}
        </span>
      </div>

      <div v-else-if="header.value === 'actions'">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "options": {
      "choose": "Choose a column",
      "domain": "Domain / Website",
      "dribbble": "Dribbble",
      "facebook": "Facebook",
      "github": "GitHub",
      "instagram": "Instagram",
      "linkedin": "LinkedIn",
      "name": "Company name",
      "pinterest": "Pinterest",
      "snapchat": "Snapchat",
      "tiktok": "TikTok",
      "twitter": "Twitter",
      "youtube": "YouTube"
    }
  },
  "es": {
    "options": {
      "choose": "Elegir una columna",
      "domain": "Dominio / Website",
      "dribbble": "Dribbble",
      "facebook": "Facebook",
      "github": "GitHub",
      "instagram": "Instagram",
      "linkedin": "LinkedIn",
      "name": "Nombre de la empresa",
      "pinterest": "Pinterest",
      "snapchat": "Snapchat",
      "tiktok": "TikTok",
      "twitter": "Twitter",
      "youtube": "YouTube"
    }
  },
  "fr": {
    "options": {
      "choose": "Choisir une colonne",
      "domain": "Domaine / Website",
      "dribbble": "Dribbble",
      "facebook": "Facebook",
      "github": "GitHub",
      "instagram": "Instagram",
      "linkedin": "LinkedIn",
      "name": "Nom d'entreprise",
      "pinterest": "Pinterest",
      "snapchat": "Snapchat",
      "tiktok": "TikTok",
      "twitter": "Twitter",
      "youtube": "YouTube"
    }
  }
}
</i18n>
